import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '../auth/auth.service';
import { AutoclassifierSdkService } from '../autoclassifier-sdk/autoclassifier-sdk.service';
import {
  Course,
  FieldWithBbox,
  TranscriptExtractionData
} from '../../models/transcriptExtractionData.model';
import { SisResponse, SisErrorCodes, BatchSisResponse } from '../../models/sis-response.model';
import { SaveTranscriptRequest } from '../../models/etrive.model';

export interface TranscriptBatchItem {
  data: TranscriptExtractionData;
  transcriptId: string;
  transcriptUrl: string;
}

@Injectable({
  providedIn: 'root'
})
export class EtriveConnectService {
  private authService = inject(AuthService);
  private autoclassifierSdkService = inject(AutoclassifierSdkService);

  /**
   * Gets authenticated access token, throws if not available
   */
  private async getAccessToken(): Promise<string> {
    const authToken = await this.authService.waitForToken();
    if (!authToken) throw new Error('Not authenticated');
    return authToken;
  }

  /**
   * Calls the eTrieve Connect source to send a single transcript data to SIS
   * @param sourceCode - The source code identifier
   * @param data - The transcript data to be sent to the source
   * @param softdocsTranscriptId - The unique identifier for the Softdocs transcript
   * @param softdocsTranscriptUrl - The URL to access the Softdocs transcript document
   * @returns A Promise that resolves with the SIS response
   */
  async callSource(
    sourceCode: string,
    data: TranscriptExtractionData,
    softdocsTranscriptId: string,
    softdocsTranscriptUrl: string
  ): Promise<SisResponse> {
    try {
      const accessToken = await this.getAccessToken();
      const payload = this.mapToSaveRequest(data, softdocsTranscriptId, softdocsTranscriptUrl);

      const response = await this.autoclassifierSdkService.etriveConnectSdk.callSource(
        accessToken,
        sourceCode,
        payload
      );

      return response as SisResponse;
    } catch (error) {
      console.error('Error in callSource:', error);
      return this.handleHttpError(error);
    }
  }

  /**
   * Calls the eTrieve Connect source to send multiple transcript data to SIS in batch
   * @param sourceCode - The source code identifier
   * @param transcripts - Array of transcripts with their data and metadata
   * @returns A Promise that resolves with an array of SIS responses
   */
  async callSourceBatch(
    sourceCode: string,
    transcripts: TranscriptBatchItem[]
  ): Promise<BatchSisResponse[]> {
    try {
      const accessToken = await this.getAccessToken();

      // Map all transcripts to the request format
      const payloads = transcripts.map(transcript =>
        this.mapToSaveRequest(transcript.data, transcript.transcriptId, transcript.transcriptUrl)
      );

      const response = await this.autoclassifierSdkService.etriveConnectSdk.callSource(
        accessToken,
        sourceCode,
        payloads
      );

      // If response is array, map transcriptIds
      if (Array.isArray(response)) {
        return response.map((res, index) => ({
          ...res,
          transcriptId: transcripts[index].transcriptId
        })) as BatchSisResponse[];
      }

      // If backend returns single response for batch, wrap it
      return transcripts.map(transcript => ({
        ...(response as SisResponse),
        transcriptId: transcript.transcriptId
      }));
    } catch (error) {
      console.error('Error in callSourceBatch:', error);

      // Return error for all transcripts
      return transcripts.map(transcript => ({
        ...this.handleHttpError(error),
        transcriptId: transcript.transcriptId
      }));
    }
  }
  /**
   * Gets the available eTrieve sources
   * @returns A Promise that resolves with the list of available sources
   */
  async getEtrieveSource(): Promise<any> {
    const accessToken = await this.getAccessToken();
    return this.autoclassifierSdkService.etriveConnectSdk.getEtrieveSource(accessToken);
  }

  /**
   * calls AutoClassifier API to get required fields for eTrieve Connect source
   */
  async getRequiredFields(): Promise<any> {
    const accessToken = await this.getAccessToken();
    return this.autoclassifierSdkService.etriveConnectSdk.getRequiredFields(accessToken);
  }

  /**
   * Handles errors and converts them to SisResponse format
   * @param error The error object caught from HTTP requests
   */
  private handleHttpError(error: any): SisResponse {
    if (error instanceof HttpErrorResponse) {
      switch (error.status) {
        case 404:
          return {
            success: false,
            statusCode: 404,
            code: SisErrorCodes.SOURCE_NOT_MAPPED,
            message:
              'No Connect Source is mapped to the "Send to SIS" step. Please contact your System Admin.'
          };

        case 500:
          return {
            success: false,
            statusCode: 500,
            code: SisErrorCodes.CONNECT_DOWN,
            message:
              'The Connect service is currently unavailable. Please contact your System Admin.'
          };

        case 401:
        case 403:
          return {
            success: false,
            statusCode: error.status,
            code: SisErrorCodes.AUTHENTICATION_ERROR,
            message: 'Failed to authenticate with the SIS. Please contact your System Admin.'
          };

        default:
          return {
            success: false,
            statusCode: error.status,
            code: SisErrorCodes.UNKNOWN_ERROR,
            message: error.message || 'An unexpected error occurred.'
          };
      }
    }

    // Non-HTTP error
    return {
      success: false,
      code: SisErrorCodes.UNKNOWN_ERROR,
      message: error?.message || 'An unexpected error occurred.'
    };
  }

  /**
   * Maps transcript data to the format required by the backend.
   * Extracts necessary values from FieldWithBbox objects and constructs
   * the payload for the POST request with flat course structure.
   * @param data - Transcript data with typed structure
   * @param softdocsTranscriptId - Document identifier in Softdocs (used as transcriptId)
   * @param softdocsTranscriptUrl - The URL to access the Softdocs transcript document
   * @returns SaveTranscriptRequest object with the new flat structure
   */
  private mapToSaveRequest(
    data: TranscriptExtractionData,
    softdocsTranscriptId: string,
    softdocsTranscriptUrl: string
  ): SaveTranscriptRequest {
    return {
      transcriptId: softdocsTranscriptId,
      softdocsTranscriptUrl: softdocsTranscriptUrl,
      student: {
        firstName: this.extractValue(data.Student?.FirstName),
        middleName: this.extractValue(data.Student?.MiddleName),
        lastName: this.extractValue(data.Student?.LastName),
        last4SSN: this.extractValue(data.Student?.Last4SSN),
        dateOfBirth: this.extractValue(data.Student?.DateOfBirth),
        email: this.extractValue(data.Student?.Email),
        studentId: this.extractValue(data.Student?.StudentId)
      },
      issuingInstitutionName: '',
      courses: this.mapCourses(data.Courses || [])
    };
  }

  /**
   * Maps courses to the flat format required by the backend
   * @param courses - Array of courses with FieldWithBbox structure
   * @param institutionId
   * @returns Array of courses in flat format with institution field
   */
  private mapCourses(courses: Course[]): SaveTranscriptRequest['courses'] {
    return courses.map(course => ({
      term: this.extractValue(course.Term),
      startDate: this.extractValue(course.StartDate),
      endDate: this.extractValue(course.EndDate),
      subject: this.extractValue(course.Subject),
      courseNumber: this.extractValue(course.CourseNumber),
      courseTitle: this.extractValue(course.CourseTitle),
      creditsEarned: this.extractNumericValue(course.CreditsEarned),
      grade: this.extractValue(course.Grade),
      points: this.extractNumericValue(course.GradePoints),
      institution: this.extractValue(course.Institution),
      institutionId: this.extractValue(course.InstitutionId)
    }));
  }

  /**
   * Extracts the value from a FieldWithBbox and converts it to string.
   * If the field is undefined or has no value, returns an empty string.
   * @param field - Field with bbox information that may be undefined
   * @returns Field value as string, or empty string if it doesn't exist
   */
  private extractValue(field: FieldWithBbox | undefined): string {
    if (!field || field.value === null || field.value === undefined) {
      return '';
    }
    return field.value.toString().trim();
  }

  /**
   * Extracts the numeric value from a FieldWithBbox.
   * Performs safe conversion to number and handles cases where the value is not numeric.
   * @param field - Field with bbox information that may contain a numeric value
   * @returns Numeric value of the field, or 0 if it cannot be converted or is undefined
   */
  private extractNumericValue(field: FieldWithBbox | undefined): number {
    if (!field || field.value === null || field.value === undefined) {
      return 0;
    }

    const value = field.value;
    const numericValue = typeof value === 'number' ? value : parseFloat(value.toString());
    return isNaN(numericValue) ? 0 : numericValue;
  }
}
